import type { FlyoverSegment, Vector3Like } from '../sceneConfig';

/**
 * Ready-made flyover generators for the FlyoverTuner panel (`?flight`).
 * Each preset derives its geometry from the scene's own anchors and
 * returns a draft `segments` array the author then tweaks by hand.
 *
 * Invariants every preset must keep:
 * - The flight starts at the scene's entry pose (the panel's "Start" row
 *   = `previewTourEnd`) — presets assume that start.
 * - The flight ENDS inside the camera limits: either back at the start
 *   pose (closing waypoint / a 360° orbit returning to its start azimuth)
 *   or at the SAME radius+height on another azimuth (Swoop Around's
 *   mirrored exit). Otherwise the first enabled `controls.update()` after
 *   the flight clamps the pose — a visible snap.
 * - No two ADJACENT spline control points may coincide — duplicates make
 *   the centripetal CatmullRom produce NaN. This is why `orbit360` has NO
 *   closing waypoint (it would duplicate the arc's last sample) and why
 *   presets bail on a near-zero radius (start pose above the pivot).
 */
export type FlyoverPresetInput = {
  /** Orbit pivot: scene's `controls.orbitCenter`, fallback start-pose target. */
  orbitCenter: Vector3Like;
  /** The scene's entry pose — presets start AND end here. */
  tourEndPosition: Vector3Like;
  tourEndTarget: Vector3Like;
};

export type FlyoverPreset = {
  id: string;
  label: string;
  /** Returns `[]` when the scene geometry can't support the preset. */
  build: (input: FlyoverPresetInput) => FlyoverSegment[];
};

/**
 * Horizontal distance below which orbit math degenerates (camera almost
 * exactly above the pivot → all arc samples coincide → spline NaN).
 */
const MIN_PRESET_RADIUS = 0.05;

/** ~45°/s — full circle in 8 s, matches the kaaba flyover's pacing. */
const orbitDuration = (angleDeg: number): number =>
  Math.round((Math.abs(angleDeg) / 45) * 10) / 10;

export const round3 = (n: number): number => Math.round(n * 1000) / 1000;

export const roundV3 = (v: Vector3Like): Vector3Like => ({
  x: round3(v.x),
  y: round3(v.y),
  z: round3(v.z),
});

const orbitParams = ({ orbitCenter, tourEndPosition }: FlyoverPresetInput) => ({
  radius: Math.hypot(
    tourEndPosition.x - orbitCenter.x,
    tourEndPosition.z - orbitCenter.z,
  ),
  height: tourEndPosition.y - orbitCenter.y,
});

/**
 * Returns a point-builder on the circle around the pivot: azimuth offset
 * is relative to the START pose's azimuth, radius/height scale the start
 * pose's ring, `heightOffset` adds absolute units on top (for zeniths).
 */
const makeCirclePoint = (input: FlyoverPresetInput) => {
  const { radius, height } = orbitParams(input);
  const startAzimuth = Math.atan2(
    input.tourEndPosition.z - input.orbitCenter.z,
    input.tourEndPosition.x - input.orbitCenter.x,
  );
  return (
    azimuthOffsetDeg: number,
    radiusScale: number,
    heightScale: number,
    heightOffset = 0,
  ): Vector3Like => {
    const azimuth = startAzimuth + (azimuthOffsetDeg * Math.PI) / 180;
    return roundV3({
      x: input.orbitCenter.x + radius * radiusScale * Math.cos(azimuth),
      y: input.orbitCenter.y + height * heightScale + heightOffset,
      z: input.orbitCenter.z + radius * radiusScale * Math.sin(azimuth),
    });
  };
};

/**
 * Ground frame for FPS-style scenes (Mina): movement is relative to the
 * start pose's LOOK direction, not a circle around a pivot. `at(f, r, u)`
 * offsets the start position by forward / right / up world units;
 * `lookFrom(pos, ahead)` returns a HORIZONTAL look target `ahead` units in
 * front of `pos` (same y) — keeps the gaze near the horizon so it can't
 * fight the scene's `verticalMin/Max` constraint on the final frame.
 * Scale unit = horizontal start→target distance.
 */
const makeGroundFrame = (input: FlyoverPresetInput) => {
  const p = input.tourEndPosition;
  const t = input.tourEndTarget;
  const dist = Math.hypot(t.x - p.x, t.z - p.z) || 1;
  const fx = (t.x - p.x) / dist;
  const fz = (t.z - p.z) / dist;
  const rx = fz; // right = forward × up(0,1,0)
  const rz = -fx;
  return {
    dist,
    at: (forward: number, right: number, up: number): Vector3Like =>
      roundV3({
        x: p.x + fx * forward + rx * right,
        y: p.y + up,
        z: p.z + fz * forward + rz * right,
      }),
    lookFrom: (pos: Vector3Like, ahead: number): Vector3Like =>
      roundV3({ x: pos.x + fx * ahead, y: pos.y, z: pos.z + fz * ahead }),
  };
};

export const FLYOVER_PRESETS: FlyoverPreset[] = [
  {
    id: 'orbit360',
    label: 'Orbit 360°',
    build: (input) => {
      const { radius, height } = orbitParams(input);
      if (radius < MIN_PRESET_RADIUS) return [];
      // Start azimuth auto-derives from the camera (= start) position;
      // 360° returns there, so the arc itself IS the closing pose.
      return [
        {
          type: 'orbit',
          center: roundV3(input.orbitCenter),
          radius: round3(radius),
          height: round3(height),
          angleDeg: 360,
          duration: orbitDuration(360),
        },
      ];
    },
  },
  {
    id: 'riseOrbitDescend',
    label: 'Rise → Orbit → Descend',
    build: (input) => {
      const { radius, height } = orbitParams(input);
      if (radius < MIN_PRESET_RADIUS) return [];
      const rise = round3(radius * 0.6);
      return [
        {
          type: 'waypoint',
          position: roundV3({
            x: input.tourEndPosition.x,
            y: input.tourEndPosition.y + rise,
            z: input.tourEndPosition.z,
          }),
          target: roundV3(input.orbitCenter),
          duration: 2,
        },
        {
          type: 'orbit',
          center: roundV3(input.orbitCenter),
          radius: round3(radius),
          height: round3(height + rise),
          angleDeg: 360,
          duration: orbitDuration(360),
        },
        {
          type: 'waypoint',
          position: roundV3(input.tourEndPosition),
          target: roundV3(input.tourEndTarget),
          duration: 2,
        },
      ];
    },
  },
  {
    id: 'swoopAround',
    label: 'Swoop Around',
    build: (input) => {
      // Cinematic S-curve: dive sideways-down-closer past the object
      // (quarter turn at 0.55× radius, half height), then rise out on the
      // OPPOSITE side at the original radius/height. All three axes move
      // at once — strong parallax, classic drone fly-by. Ends mirrored,
      // not at the start: same radius/height as the entry pose, so the
      // final frame is equally inside the camera limits (no finish snap).
      const { radius } = orbitParams(input);
      if (radius < MIN_PRESET_RADIUS) return [];
      const at = makeCirclePoint(input);
      const center = roundV3(input.orbitCenter);
      return [
        {
          type: 'waypoint',
          position: at(90, 0.55, 0.5),
          target: center,
          duration: 2.5,
        },
        {
          type: 'waypoint',
          position: at(180, 1, 1),
          target: center,
          duration: 2.5,
        },
      ];
    },
  },
  {
    id: 'pushIn',
    label: 'Push In',
    build: (input) => {
      // Slow dolly-in with a sideways drift: approaching along a gentle
      // arc (mid point at +15°, final at +25°) instead of a cheap-looking
      // straight radial zoom. Ends on a close-up frame (0.45× radius).
      const { radius } = orbitParams(input);
      if (radius < MIN_PRESET_RADIUS) return [];
      const at = makeCirclePoint(input);
      const center = roundV3(input.orbitCenter);
      return [
        {
          type: 'waypoint',
          position: at(15, 0.7, 0.85),
          target: center,
          duration: 2,
        },
        {
          type: 'waypoint',
          position: at(25, 0.45, 0.7),
          target: center,
          duration: 2,
        },
      ];
    },
  },
  {
    id: 'skyfall',
    label: 'Skyfall',
    build: (input) => {
      // Rise to a near-zenith top-down view of the scene, then dive back
      // to the mirrored side of the start ring. The zenith stays slightly
      // off the pivot axis (0.15× radius): a dead-vertical look-at has
      // its direction parallel to camera.up → lookAt degenerates/flips.
      const { radius } = orbitParams(input);
      if (radius < MIN_PRESET_RADIUS) return [];
      const at = makeCirclePoint(input);
      const center = roundV3(input.orbitCenter);
      return [
        {
          type: 'waypoint',
          position: at(90, 0.15, 1, round3(radius * 1.1)),
          target: center,
          duration: 3,
        },
        {
          type: 'waypoint',
          position: at(180, 1, 1),
          target: center,
          duration: 3,
        },
      ];
    },
  },
  // ── FPS-scene presets (Mina) ──────────────────────────────────────────
  // First-person scenes look BAD with orbits (feels like being spun) and
  // can't tilt to the sky/floor (verticalMin/Max). These glide relative to
  // the start pose's look direction, gaze held horizontal. Distances scale
  // to the scene, but they move BLIND (no knowledge of tents / lanes) —
  // treat as a direction scaffold and nudge the reach in the panel.
  {
    id: 'glideForward',
    label: 'Glide Forward',
    build: (input) => {
      // Dolly forward along the line of sight — rows slide past for parallax.
      const g = makeGroundFrame(input);
      if (g.dist < MIN_PRESET_RADIUS) return [];
      const p1 = g.at(g.dist * 0.3, 0, 0);
      const p2 = g.at(g.dist * 0.6, 0, 0);
      return [
        { type: 'waypoint', position: p1, target: g.lookFrom(p1, g.dist), duration: 2.5 },
        { type: 'waypoint', position: p2, target: g.lookFrom(p2, g.dist), duration: 2.5 },
      ];
    },
  },
  {
    id: 'driftSideways',
    label: 'Drift Sideways',
    build: (input) => {
      // Track sideways with the gaze kept forward — strong lateral parallax.
      const g = makeGroundFrame(input);
      if (g.dist < MIN_PRESET_RADIUS) return [];
      const p1 = g.at(0, g.dist * 0.25, 0);
      const p2 = g.at(0, g.dist * 0.5, 0);
      return [
        { type: 'waypoint', position: p1, target: g.lookFrom(p1, g.dist), duration: 2.5 },
        { type: 'waypoint', position: p2, target: g.lookFrom(p2, g.dist), duration: 2.5 },
      ];
    },
  },
  {
    id: 'revealPan',
    label: 'Reveal Pan',
    build: (input) => {
      // Near-static slow pan: the gaze sweeps left→right across the valley
      // while the camera creeps forward a touch (so waypoints don't land on
      // the start position → duplicate spline point → NaN).
      const g = makeGroundFrame(input);
      if (g.dist < MIN_PRESET_RADIUS) return [];
      return [
        { type: 'waypoint', position: g.at(g.dist * 0.1, -g.dist * 0.12, 0), target: g.at(g.dist, -g.dist * 0.6, 0), duration: 2.5 },
        { type: 'waypoint', position: g.at(g.dist * 0.2, g.dist * 0.12, 0), target: g.at(g.dist, g.dist * 0.6, 0), duration: 2.5 },
      ];
    },
  },
];
